import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classNames from 'classnames';
import React, { useState, JSX } from 'react';
import { ApiBookedItem, ApiItemType, ApiNote, ApiNoteType } from '@ibe/api';
import Keys from '../../../_Translations/generated/iso-items-base.en.t.json.keys';
import CheckoutListTraveler from './Component/Traveler';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/iso-items-base.en.t.json';
import { BookedItemComponentProps } from '../../../Models/ItemRegistry';
import NotesFormatter from '../../Components/NotesFormatter/NotesFormatter';
import useConfig from '../../Hooks/useConfig';

interface Props {
  info: React.ReactNode;
  ssrs?: React.ReactNode;
  seatTabContent?: React.ReactNode;
}

const SummaryTabs = (props: Props & BookedItemComponentProps): JSX.Element => {
  const { t } = useTranslation('iso-items-base', fallback, Keys);

  const {
    item,
    showInfo,
    showTraveler,
    showSsr,
    showSeats,
    booking,
    info,
    ssrs,
    seatTabContent
  } = props;

  let defaultActiveTab = '';
  if (showTraveler) {
    defaultActiveTab = 'traveler';
  } else if (showInfo) {
    defaultActiveTab = 'info';
  } else if (showSsr) {
    defaultActiveTab = 'ssr';
  }

  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const config = useConfig();
  const { filterNote, showTitle } = config.summaryComponentParams?.packageItemType || {};
  const toggleTab = (tab: string): void => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getPackageNotes = (packageId: string): string[] => {
    const summaryNotes: string[] = [];
    if (!booking || !booking.items || !booking.items.length) {
      return summaryNotes;
    }
    booking.bookedItems
      .filter(value => value.relatedBookedItemId === packageId)
      .forEach(itemNote => {
        if (itemNote) {
          collectNotesPerSubType(itemNote, summaryNotes);
        }
      });
    return summaryNotes;
  };

  const collectNotesPerSubType = (itemNote: ApiBookedItem, notes: string[]): void => {
    let name = '';
    const itemNotes: string[] = [];
    if (itemNote.itemType === ApiItemType.TRANSFER) {
      name = t(Keys.subItemType_TRANSFER);
      if (itemNote.subItemType === ApiItemType.CHAUFFEUREDSERVICE) {
        name = t(Keys.subItemType_CHAUFFEURED_SERVICE);
      }
      if (itemNote.subItemType === ApiItemType.AIRPORTTRANSFER) {
        name = t(Keys.subItemType_AIRPORT_TRANSFER);
      }
    }
    if (itemNote.itemType === ApiItemType.EXTRA) {
      name = t(Keys.subItemType_TOUR);
      if (itemNote.subItemType === ApiItemType.DINING) {
        name = t(Keys.subItemType_DINING);
      }
      if (itemNote.subItemType === ApiItemType.LOUNGEACCESS) {
        name = t(Keys.subItemType_LOUNGE_ACCESS);
      }
      if (itemNote.subItemType === ApiItemType.MEETANDGREET) {
        name = t(Keys.subItemType_MEET_AND_GREET);
      }
    }
    if (itemNote.itemType === ApiItemType.HOTEL) {
      name = t(Keys.subItemType_HOTEL);
    }
    if (itemNote.itemType === ApiItemType.PACKAGE && showTitle) {
      name = t(Keys.subItemType_PACKAGE);
    }
    collectNotes(itemNotes, itemNote.notes, name);
    notes.push(...itemNotes);
  };

  const collectNotes = (notes: string[], itemNotes: ApiNote[] | undefined, name?: string): void => {
    if (!itemNotes || !itemNotes.length) {
      return;
    }
    let first = true;

    const filterApiNote = (note: ApiNote): boolean => {
      if (!filterNote) return true;
      if (name == t(Keys.subItemType_PACKAGE)) {
        return true;
      }
      return note && note.type === ApiNoteType.INFO;
    };

    itemNotes
      .filter(note => filterApiNote(note))
      .forEach(note => {
        if (first && name) {
          notes.push(name);
          first = false;
        }
        notes.push(note.text);
      });
  };

  const getBookingNotes = (): string[] => {
    if (item.itemType === ApiItemType.PACKAGE) {
      return getPackageNotes(item.id);
    }
    const notes: string[] = [];
    item.notes?.map(note => notes.push(note.text));
    return notes;
  };

  const notes = getBookingNotes();

  return (
    <div className="iso__nav__tabs-container">
      <Nav tabs>
        {showTraveler && (
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'traveler' })}
              onClick={(): void => toggleTab('traveler')}
            >
              {t(Keys.traveler)}
            </NavLink>
          </NavItem>
        )}
        {showInfo && (
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'info' })}
              onClick={(): void => toggleTab('info')}
            >
              {t(Keys.info)}
            </NavLink>
          </NavItem>
        )}
        {showSsr && (
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'ssr' })}
              onClick={(): void => toggleTab('ssr')}
            >
              {t(Keys.ssr)}
            </NavLink>
          </NavItem>
        )}
        {showSeats && (
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'seats' })}
              onClick={(): void => toggleTab('seats')}
            >
              {t(Keys.seats)}
            </NavLink>
          </NavItem>
        )}
        {notes && notes.length > 0 && (
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'notes' })}
              onClick={(): void => toggleTab('notes')}
            >
              {t(Keys.notes)}
            </NavLink>
          </NavItem>
        )}
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="traveler">
          <CheckoutListTraveler
            traveler={
              Object.keys(item.priceByPersonId).length !== 0
                ? booking.travelers.filter(traveler =>
                    Object.keys(item.priceByPersonId).includes(traveler.id)
                  )
                : booking.travelers.filter(traveler => {
                    const foundItem = booking.bookedItems.find(
                      bookedItem => bookedItem.relatedBookedItemId === item.id
                    );
                    return !foundItem
                      ? false
                      : Object.keys(foundItem.priceByPersonId).includes(traveler.id);
                  })
            }
          />
        </TabPane>
        <TabPane tabId="info">{info}</TabPane>
        <TabPane tabId="ssr">
          {ssrs || (
            <div>
              {item.specialRequests &&
                item.specialRequests.length > 0 &&
                item.specialRequests.map(ssr => {
                  return (
                    <div key={JSON.stringify(ssr)}>
                      {ssr.description}
                      {ssr.amount ? ` (${ssr.amount})` : ''}
                    </div>
                  );
                })}
            </div>
          )}
        </TabPane>
        <TabPane tabId="seats">{seatTabContent && <>{seatTabContent}</>}</TabPane>
        <TabPane tabId="notes">
          <div>
            <NotesFormatter notes={notes} />
          </div>
        </TabPane>
      </TabContent>
    </div>
  );
};

export default SummaryTabs;
